import { useTranslations } from 'next-intl';
import type { ChangeEvent, KeyboardEvent } from 'react';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';

interface CreateFolderDialogProps {
  open: boolean;
  folderName: string;
  creatingFolder: boolean;
  onOpenChange: (open: boolean) => void;
  onFolderNameChange: (name: string) => void;
  onCreate: () => void | Promise<void>;
}

export const CreateFolderDialog = ({
  open,
  folderName,
  creatingFolder,
  onOpenChange,
  onFolderNameChange,
  onCreate,
}: CreateFolderDialogProps) => {
  const t = useTranslations();

  const handleCancel = (): void => {
    onOpenChange(false);
    onFolderNameChange('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key !== 'Enter' || creatingFolder) return;
    onCreate();
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      onFolderNameChange('');
    }
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    onFolderNameChange(event.target.value);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent inheritTheme className="w-[92%] max-w-[520px] p-6" disableOverlayClose={creatingFolder}>
        <DialogHeader>
          <DialogTitle>{t('claw.file.newFolder')}</DialogTitle>
        </DialogHeader>
        <Input
          autoFocus
          value={folderName}
          onChange={handleNameChange}
          onKeyDown={handleKeyDown}
          className="h-10"
          placeholder={t('claw.file.newFolderPlaceholder')}
        />
        <DialogFooter className="space-x-2">
          <Button
            variant="text"
            size="sm"
            className="h-9 px-4 text-system-white64"
            disabled={creatingFolder}
            onClick={handleCancel}
          >
            {t('common.dialog.cancel')}
          </Button>
          <Button
            variant="solid"
            size="sm"
            className="h-9 px-4"
            loading={creatingFolder}
            onClick={() => {
              onCreate();
            }}
          >
            {t('claw.file.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
